/**
 * Shopping list detail — DESIGN_GUIDELINES.md §9.3.
 *
 * Delivered items are separated from cupboard items, because HelloFresh does not deliver the
 * latter and buying them is the user's choice.
 */

import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  Alert,
  Anchor,
  Card,
  Checkbox,
  Group,
  Loader,
  Progress,
  Stack,
  Text,
  Title,
} from '@mantine/core';
import { PageShell } from '../components/PageShell';
import { fetchList, setItemChecked, type SessionUser, type ShoppingListItem } from '../lib/api';
import { useAsync } from '../lib/useAsync';

export function ListDetailPage({
  user,
  onSignOut,
}: {
  user: SessionUser;
  onSignOut: () => void;
}) {
  const { id = '' } = useParams();
  const detail = useAsync(() => fetchList(id), [id]);
  const [pending, setPending] = useState<Set<string>>(new Set());

  async function toggle(item: ShoppingListItem, next: boolean) {
    if (!detail.data) return;
    // Optimistic: ticking a box must feel instant.
    detail.setData({
      ...detail.data,
      items: detail.data.items.map((row) => (row.id === item.id ? { ...row, isChecked: next } : row)),
    });
    setPending((set) => new Set(set).add(item.id));
    try {
      await setItemChecked(id, item.id, next);
    } catch {
      detail.reload();
    } finally {
      setPending((set) => {
        const copy = new Set(set);
        copy.delete(item.id);
        return copy;
      });
    }
  }

  if (detail.loading) {
    return (
      <PageShell user={user} onSignOut={onSignOut}>
        <Group justify="center" py="xl">
          <Loader />
        </Group>
      </PageShell>
    );
  }

  if (detail.error || !detail.data) {
    return (
      <PageShell user={user} onSignOut={onSignOut}>
        <Alert color="red" variant="light" role="alert">
          {detail.error ?? 'List not found.'}
        </Alert>
        <Anchor component={Link} to="/lists" mt="md" display="block">
          ← Back to your lists
        </Anchor>
      </PageShell>
    );
  }

  const delivered = detail.data.items.filter((item) => !item.isPantry);
  const pantry = detail.data.items.filter((item) => item.isPantry);
  const checkedCount = detail.data.items.filter((item) => item.isChecked).length;

  return (
    <PageShell
      user={user}
      onSignOut={onSignOut}
      title={
        <Stack gap={2}>
          <Anchor component={Link} to="/lists" size="sm" c="var(--ff-muted)" td="none">
            ← Back
          </Anchor>
          <Title order={2}>{detail.data.list.name}</Title>
        </Stack>
      }
    >
      <Stack gap="xs" maw={480}>
        <Text size="sm" c="var(--ff-muted)">
          {checkedCount} of {detail.data.items.length} items ticked
        </Text>
        <Progress
          value={detail.data.items.length === 0 ? 0 : (checkedCount / detail.data.items.length) * 100}
          color="hfGreen"
          radius="xl"
        />
      </Stack>

      <Stack gap="sm">
        <Title order={3}>To buy</Title>
        {delivered.length === 0 ? (
          <Text c="var(--ff-muted)" size="sm">
            Nothing to buy for this list.
          </Text>
        ) : (
          delivered.map((item) => (
            <ItemRow
              key={item.id}
              item={item}
              busy={pending.has(item.id)}
              onToggle={(next) => void toggle(item, next)}
            />
          ))
        )}
      </Stack>

      {pantry.length > 0 ? (
        <Card
          radius="lg"
          padding="md"
          style={{ background: 'var(--ff-green-050)', border: '1px dashed var(--ff-green-700)' }}
        >
          <Stack gap="sm">
            <Title order={3}>From your cupboard</Title>
            <Text size="xs" c="var(--ff-muted)">
              Not delivered with the recipe — buy only if you need them.
            </Text>
            {pantry.map((item) => (
              <ItemRow
                key={item.id}
                item={item}
                busy={pending.has(item.id)}
                onToggle={(next) => void toggle(item, next)}
              />
            ))}
          </Stack>
        </Card>
      ) : null}
    </PageShell>
  );
}

function ItemRow({
  item,
  busy,
  onToggle,
}: {
  item: ShoppingListItem;
  busy: boolean;
  onToggle: (next: boolean) => void;
}) {
  return (
    <Group
      justify="space-between"
      wrap="nowrap"
      gap="sm"
      style={{ minHeight: 44, cursor: 'pointer' }}
      onClick={() => !busy && onToggle(!item.isChecked)}
    >
      <Checkbox
        checked={item.isChecked}
        onChange={(event) => onToggle(event.currentTarget.checked)}
        disabled={busy}
        label={
          <Text
            size="sm"
            td={item.isChecked ? 'line-through' : undefined}
            c={item.isChecked ? 'var(--ff-muted)' : 'var(--ff-ink)'}
          >
            {item.displayName}
          </Text>
        }
        styles={{ body: { alignItems: 'center' } }}
      />
      <Text
        fw={600}
        size="sm"
        style={{ whiteSpace: 'nowrap' }}
        c={item.isChecked ? 'var(--ff-muted)' : 'var(--ff-ink)'}
      >
        {item.amountText}
      </Text>
    </Group>
  );
}
